import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild
} from '@angular/core';
import {
  AbstractControl, ControlContainer,
  ControlValueAccessor, FormControl, FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {tap} from 'rxjs/operators';
import {Subscription} from 'rxjs';

const VAR_OPENING_TAGS = '[[';
const VAR_CLOSING_TAGS = ']]';

@Component({
  selector: 'app-number-selector-input',
  templateUrl: './number-selector-input.component.html',
  styleUrls: ['./number-selector-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: NumberSelectorInputComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: NumberSelectorInputComponent
    }
  ]
})
export class NumberSelectorInputComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, Validator {
  @Input() public controlLabel: string;
  @Input() public formControlName: string;
  @ViewChild('controlInput') controlInput: ElementRef;

  private increment = 1;
  public savedValue: string | number = 0;
  private touched = false;
  public disabled = false;
  public isControlValid = true;
  public isVariable: boolean;
  private statusChangesSubscription: Subscription;

  constructor(@Optional() private controlContainer: ControlContainer) { }

  ngOnInit(): void {
    this.statusChangesSubscription = this.listenForControlStatusChanges();
  }

  ngOnDestroy(): void {
    this.statusChangesSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.listenForKeyboardInput();
    this.validate(this.control);
  }

  /**
   * gets the initial validity of the control and listens for future changes
   * @private
   */
  private listenForControlStatusChanges() {
    this.hasErrorsCheck();
    return this.control.statusChanges.pipe(
      tap(isValid => {
        if (isValid === 'VALID') {
          this.isControlValid = true;
        }
        if (isValid === 'INVALID') {
          this.isControlValid = false;
        }
      }),
    ).subscribe();
  }

  /**
   * gets the initial validity of the control
   * @private
   */
  private hasErrorsCheck() {
    if (this.control.errors) {
      this.isControlValid = false;
    } else {
      this.isControlValid = true;
    }
  }

  private listenForKeyboardInput() {
    this.controlInput.nativeElement.addEventListener('keyup', (data) => {
      if (!this.disabled) {
        this.markAsTouched();
        if (typeof this.controlInput.nativeElement.value === 'number') {
          this.savedValue = +this.controlInput.nativeElement.value;
        }
        if (typeof this.controlInput.nativeElement.value === 'string') {
          this.savedValue = this.controlInput.nativeElement.value;
        }
        this.onChange(this.savedValue);
      }
    });
  }

  get form(): FormGroup {
    return this.controlContainer.control as FormGroup;
  }

  get control(): FormControl {
    return this.form.get(this.formControlName) as FormControl;
  }

  onChange = (quantity) => { };

  onTouched = () => { };

  onAdd() {
    this.markAsTouched();
    if (!this.disabled) {
      if (typeof this.savedValue === 'number') {
        this.savedValue += this.increment;
      } else {
        this.savedValue = +this.savedValue;
        if (isNaN(this.savedValue)) {
          this.savedValue = 0;
        } else {
          this.savedValue += this.increment;
        }
      }
      this.onChange(this.savedValue);
    }
  }

  onRemove() {
    this.markAsTouched();
    if (!this.disabled) {
      if (typeof this.savedValue === 'number') {
        this.savedValue -= this.increment;
      } else {
        this.savedValue = +this.savedValue;
        if (isNaN(this.savedValue)) {
          this.savedValue = 0;
        } else {
          this.savedValue -= this.increment;
        }
      }
      this.onChange(this.savedValue);
    }
  }

  writeValue(quantity: number) {
    this.savedValue = quantity;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const valueToValidate = control.value;
    this.isVariable = false;
    if (typeof valueToValidate !== 'number' && valueToValidate !== null) {
      const int = +(valueToValidate.toString());
      if (isNaN(int)) {
        if (valueToValidate.startsWith(VAR_OPENING_TAGS) && valueToValidate.endsWith(VAR_CLOSING_TAGS)) {
          this.isVariable = true;
          return null;
        } else if (!valueToValidate.startsWith(VAR_OPENING_TAGS) || !valueToValidate.endsWith(VAR_CLOSING_TAGS)) {
          this.isVariable = true;
          return { invalidVariable: { quantity: valueToValidate, message: `${this.controlLabel} must be a number or a variable eg. [[variableName]]` } };
        }
      } else {
        this.isVariable = false;
        return null;
      }
    }
  }

}
